// frontend/src/components/courses/CourseCard.tsx

import { Link } from 'react-router-dom';
import { Course } from '../../types/courses.types';
import { useAuthStore } from '../../store/authStore';

const CATEGORY_ICON: Record<string, string> = {
  python: '🐍',
  game: '🎮',
  ai: '🧠',
  web: '🌐',
  scratch: '🎨',
};

const CATEGORY_GRADIENT: Record<string, string> = {
  python: 'gradient-1',
  game: 'gradient-3',
  ai: 'gradient-purple-pink',
  web: 'gradient-blue-cyan',
  scratch: 'gradient-2',
};

const DIFFICULTY_LABEL: Record<string, string> = {
  beginner: 'Başlangıç',
  intermediate: 'Orta',
  advanced: 'İleri',
};

interface CourseCardProps {
  course: Course;
}

export function CourseCard({ course }: CourseCardProps) {
  const profile = useAuthStore((s) => s.profile);
  const icon = CATEGORY_ICON[course.category] ?? '📘';
  const gradientClass = CATEGORY_GRADIENT[course.category] ?? 'gradient-1';
  const progress = course.progressPercent ?? 0;

  // Kullanıcının yaş grubuna göre erişim kontrolü
  const userAge = profile?.age;
  let isAgeIncompatible = false;

  if (userAge && course.age_category) {
    if (course.age_category === '7-9' && (userAge < 7 || userAge > 9)) {
      isAgeIncompatible = true;
    } else if (course.age_category === '10-13' && (userAge < 10 || userAge > 13)) {
      isAgeIncompatible = true;
    } else if (course.age_category === '14-17' && (userAge < 14 || userAge > 17)) {
      isAgeIncompatible = true;
    }
  }

  if (isAgeIncompatible) {
    return (
      <div 
        className="course-card" 
        style={{ 
          opacity: 0.65, 
          cursor: 'not-allowed', 
          pointerEvents: 'none', 
          position: 'relative',
          filter: 'grayscale(0.6)'
        }}
      >
        <div className={`course-card-image ${gradientClass}`} style={course.cover_image_url ? { padding: 0, overflow: 'hidden' } : undefined}>
          {course.age_category && (
            <div className="course-card-age-tag" style={{ background: '#ef4444', color: '#fff' }}>
              🔒 {course.age_category} Yaş
            </div>
          )}
          {course.cover_image_url ? (
            <img src={course.cover_image_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            icon
          )}
        </div>
        <div className="course-card-content">
          <div className="course-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {course.title}
          </div>
          <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 800, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            🚫 Yaş grubunuza uygun değil ({course.age_category} Yaş)
          </div>
          <div className="course-card-meta" style={{ marginTop: 8 }}>
            <span>📚 {course.lessonCount ?? 0} Ders</span>
            <span>🎯 {DIFFICULTY_LABEL[course.difficulty] ?? course.difficulty}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link to={`/courses/${course.id}`} className="course-card">
      <div className={`course-card-image ${gradientClass}`} style={course.cover_image_url ? { padding: 0, overflow: 'hidden' } : undefined}>
        {course.age_category && (
          <div className="course-card-age-tag">
            👶 {course.age_category} Yaş
          </div>
        )}
        {course.cover_image_url ? (
          <img src={course.cover_image_url} alt={course.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          icon
        )}
      </div>
      <div className="course-card-content">
        <div className="course-card-title">{course.title}</div>
        <div className="course-card-meta">
          <span>📚 {course.lessonCount ?? 0} Ders</span>
          {course.estimatedHours ? <span>⏱️ {course.estimatedHours} Saat</span> : null}
          <span>🎯 {DIFFICULTY_LABEL[course.difficulty] ?? course.difficulty}</span>
        </div>
        <div className="course-card-progress">
          <div className="course-card-progress-row">
            <span>İlerleme</span>
            <span className="course-card-progress-value">%{progress}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
    </Link>
  );
}

